import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { Star } from 'lucide-react';
import { ReconhecerProdutoCamera } from '../../components/reconhecimento/ReconhecerProdutoCamera';
import { useAuth } from '../../lib/auth';

// Estoque por Prateleira — a tela de CONTAGEM ([DA-338]).
//
// POR QUE existe: a `Localizacoes por Produtos` (DA-327) e a RONDA (escolhe o lugar, ve quem mora
// nele). Esta e a PLANILHA: uma linha por (produto x prateleira), todas juntas, pra encher em massa
// os dois numeros que nasceram vazios na DA-328. Medido em 18/07: de **1.152 vinculos, 2 tinham
// quantidade contada** — e a cascata da venda (Fase 2) nao tem de onde drenar enquanto isso.
// A pegadinha e a mesma do Minimo de Compra ([DA-332]): o campo e a gravacao ja existiam; o que
// faltava era um jeito de ENCHER (um input por vez na ficha, ninguem preenche 1.152).
//
// A ORDEM e a do CODIGO INTERNO (o Ruan pediu em 18/07, no lugar da ordem da ronda que eu tinha
// posto): o codigo e o que ele le na etiqueta e no produto, entao achar a linha e procurar o numero
// que esta na mao. Pra contar um corredor inteiro, o caminho e filtrar pelo lugar.
//
// `—` vs `0` ([DA-328]): vazio = **ninguem contou**; zero = **contado e esta vazio**. So o zero
// dispara reposicao. Por isso o campo aceita ser LIMPO (volta pro `—`) — digitou errado, apaga, e
// a prateleira volta pra fila em vez de mentir que foi conferida.

// "So multi-lugar" nasce LIGADO (Ruan, 23/07): produto de um lugar so nunca precisa de reposicao —
// quando acaba, e compra. O recorte abre a tela nos 279 vinculos que rendem trabalho, em vez dos
// 1.172, e cresce sozinho conforme os lugares vao sendo cadastrados. E um FILTRO, nao uma regra: o
// checkbox desliga e a planilha inteira volta — senao os 893 produtos de um lugar (55 deles ainda
// sem contagem) ficariam sem porta de contagem em massa, e o estoque geral deles nao tem outra
// origem desde a [DA-339].

const KEY_ESTADO = 'estoque.prateleiras.estado';

interface EstadoSalvo {
  busca: string;
  lugarId: number | null;
  soNaoContados: boolean;
  mostrarInativos: boolean;
  soMultiLugar: boolean;
}

function lerEstado(): EstadoSalvo {
  const def: EstadoSalvo = { busca: '', lugarId: null, soNaoContados: false, mostrarInativos: false, soMultiLugar: true };
  try {
    const raw = sessionStorage.getItem(KEY_ESTADO);
    if (raw) return { ...def, ...(JSON.parse(raw) as Partial<EstadoSalvo>) };
  } catch { /* JSON corrompido: default em vez de derrubar a tela */ }
  return def;
}

interface Lugar { id: number; codigo: string; descricao: string | null; produtos_ativos: number }

interface Vinculo {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  status: string;
  estoque_atual: string;
  estoque_minimo: string;
  localizacao_id: number;
  localizacao_codigo: string;
  principal: boolean;
  quantidade: string | null;
  quantidade_minima: string | null;
  lugares_do_produto: number;
}

// `numeric` do Postgres chega como string ("3.000"). `parseQtd` NAO serve aqui (viraria 3000).
const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtBR = (v: string | null): string => (v === null || v === '' ? '' : String(num(v)).replace('.', ','));

const idLinha = (v: Vinculo) => `${v.produto_id}-${v.localizacao_id}`;

export function EstoquePorPrateleira() {
  // A coluna Est. Atual (Geral) e' o TOTAL contabil, e desde 20/08/2026 so admin o move. Pro
  // estoquista ela vira leitura: a porta dele e' a coluna de CONTAGEM ao lado, que e' onde a
  // divergencia entre o sistema e a prateleira fica registrada em vez de ser apagada.
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const toast = useToast();
  const inicial = useRef(lerEstado()).current;

  const [lugares, setLugares] = useState<Lugar[]>([]);
  const [linhas, setLinhas] = useState<Vinculo[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [busca, setBusca] = useState(inicial.busca);
  const [lugarId, setLugarId] = useState<number | null>(inicial.lugarId);
  const [soNaoContados, setSoNaoContados] = useState(inicial.soNaoContados);
  const [mostrarInativos, setMostrarInativos] = useState(inicial.mostrarInativos);
  const [soMultiLugar, setSoMultiLugar] = useState(inicial.soMultiLugar);
  const [celulaFocada, setCelulaFocada] = useState<string | null>(null);

  useEffect(() => {
    sessionStorage.setItem(KEY_ESTADO, JSON.stringify({ busca, lugarId, soNaoContados, mostrarInativos, soMultiLugar }));
  }, [busca, lugarId, soNaoContados, mostrarInativos, soMultiLugar]);

  useEffect(() => {
    api.get<Lugar[]>('/api/localizacoes').then(setLugares).catch(() => setLugares([]));
  }, []);

  // Busca com atraso: digitar filtra no BANCO (a lista inteira nao vem pro cliente), entao sem o
  // atraso cada tecla vira uma consulta.
  const [buscaAtrasada, setBuscaAtrasada] = useState(busca);
  useEffect(() => {
    const t = setTimeout(() => setBuscaAtrasada(busca), 300);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    const p = new URLSearchParams();
    if (buscaAtrasada.trim()) p.set('busca', buscaAtrasada.trim());
    if (lugarId) p.set('localizacao_id', String(lugarId));
    if (soNaoContados) p.set('sem_contagem', 'true');
    if (mostrarInativos) p.set('incluir_inativos', 'true');
    if (soMultiLugar) p.set('so_multi_lugar', 'true');
    api.get<Vinculo[]>(`/api/localizacoes/vinculos?${p.toString()}`)
      .then((r) => { if (!cancelado) setLinhas(r); })
      .catch(() => { if (!cancelado) setLinhas([]); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [buscaAtrasada, lugarId, soNaoContados, mostrarInativos, soMultiLugar]);

  const semContagem = useMemo(() => linhas.filter((l) => l.quantidade === null).length, [linhas]);

  const grid = useGridNavegavel<Vinculo>({
    itens: linhas,
    // Id COMPOSTO: o mesmo produto aparece numa linha por prateleira. Com `produto_id` sozinho, marcar
    // uma linha marcaria todas as prateleiras dele.
    getId: idLinha,
    ativarPorEnter: false,
  });

  // Espelho do LUGAR UNICO ([DA-366]): quando o produto so mora nesta prateleira, a contagem dela e
  // o Est. Atual (Geral) sao o MESMO numero, e o backend move os dois. As duas colunas estao lado a
  // lado nesta tela — sem espelhar aqui tambem, uma das duas mentiria ate o F5.
  const lugarUnico = (v: Vinculo) => v.lugares_do_produto === 1;

  // Grava um numero da PRATELEIRA (o vinculo). `null` limpa a contagem — menos no lugar unico, onde
  // o backend recusa (nao ha o que "descontar": o total ja responde quanto tem).
  async function salvarVinculo(v: Vinculo, campo: 'quantidade' | 'quantidade_minima', valor: number | null) {
    const anterior = v[campo];
    const novo = valor === null ? null : String(valor);
    const arrastaTotal = campo === 'quantidade' && lugarUnico(v) && novo !== null;
    setLinhas((atual) => atual.map((l) => (idLinha(l) === idLinha(v)
      ? { ...l, [campo]: novo, ...(arrastaTotal ? { estoque_atual: novo } : {}) }
      : l)));
    try {
      await api.patch(`/api/localizacoes/${v.localizacao_id}/produtos/${v.produto_id}`, { [campo]: valor });
    } catch (e: any) {
      setLinhas((atual) => atual.map((l) => (idLinha(l) === idLinha(v)
        ? { ...l, [campo]: anterior, ...(arrastaTotal ? { estoque_atual: v.estoque_atual } : {}) }
        : l)));
      toast.erro(e?.message || 'Nao foi possivel salvar');
    }
  }

  // Grava um numero do PRODUTO. Reflete em TODAS as linhas dele — o mesmo produto pode estar em
  // varias prateleiras, e sem isso a tela mostraria dois totais diferentes pro mesmo produto.
  async function salvarProduto(v: Vinculo, campo: 'estoque_atual' | 'estoque_minimo', valor: number) {
    const anterior = v[campo];
    const novo = String(valor);
    const arrastaPrateleira = campo === 'estoque_atual' && lugarUnico(v);
    setLinhas((atual) => atual.map((l) => (l.produto_id === v.produto_id
      ? { ...l, [campo]: novo, ...(arrastaPrateleira ? { quantidade: novo } : {}) }
      : l)));
    try {
      await api.patch(`/api/produtos/${v.produto_id}`, { [campo]: novo });
    } catch (e: any) {
      setLinhas((atual) => atual.map((l) => (l.produto_id === v.produto_id
        ? { ...l, [campo]: anterior, ...(arrastaPrateleira ? { quantidade: v.quantidade } : {}) }
        : l)));
      toast.erro(e?.message || 'Nao foi possivel salvar');
    }
  }

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">Estoque por Prateleira</h2>
      </div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Uma linha por produto em cada prateleira, na ordem do codigo. <strong>—</strong> quer dizer que
        ninguem contou; <strong>0</strong> quer dizer que foi contado e esta vazio.
        {soMultiLugar && ' Mostrando so quem mora em mais de um lugar — os de um lugar so, quando acabam, sao compra.'}
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input
          type="search" className="busca" placeholder="Buscar produto, codigo ou lugar..."
          value={busca} onChange={(e) => setBusca(e.target.value)}
          style={{ width: 320 }}
        />
        {/* Contagem e o outro fluxo em que a pessoa esta fisicamente com a peca na mao
            (DA-392). Aqui a camera nao adiciona nada: ela PREENCHE A BUSCA com o codigo do
            produto reconhecido, e a tela filtra como se tivesse sido digitado. Sem
            `somenteVendaveis` — item de Uso/Consumo tambem mora em prateleira e precisa
            ser contado. */}
        <ReconhecerProdutoCamera
          tela="conferencia"
          posicao="solto"
          onEscolher={(p) => setBusca(p.codigo_interno ?? p.descricao)}
        />
        <select value={lugarId ?? ''} onChange={(e) => setLugarId(e.target.value ? Number(e.target.value) : null)} style={{ minWidth: 200 }}>
          <option value="">Todos os lugares</option>
          {lugares.map((l) => (
            <option key={l.id} value={l.id}>{l.codigo}{l.descricao ? ` — ${l.descricao}` : ''} ({l.produtos_ativos})</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }} title="Produto que mora num lugar so nunca precisa de reposicao: quando acaba, e compra. Desligue pra ver a planilha inteira e contar os demais.">
          <input type="checkbox" checked={soMultiLugar} onChange={(e) => setSoMultiLugar(e.target.checked)} />
          So os de mais de um lugar
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
          <input type="checkbox" checked={soNaoContados} onChange={(e) => setSoNaoContados(e.target.checked)} />
          So os nao contados
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
          <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
          Mostrar inativos
        </label>
        <span className="muted" style={{ fontSize: 13, marginLeft: 'auto' }}>
          {linhas.length} linha(s)
          {semContagem > 0 && (
            <span style={{ background: '#f97316', color: '#000', fontWeight: 700, borderRadius: 3, padding: '1px 6px', marginLeft: 8 }}>
              {semContagem} sem contagem
            </span>
          )}
        </span>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : linhas.length === 0 ? (
        <p className="muted">
          Nenhum vinculo encontrado com esses filtros.
          {soMultiLugar && ' Se o produto que voce procura mora num lugar so, desmarque "So os de mais de um lugar".'}
        </p>
      ) : (
        <table className="tabela">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Foto</th>
              <th style={{ width: 80 }}>Codigo</th>
              <th>Descricao</th>
              <th style={{ width: 110 }}>Localizacao</th>
              <th style={{ width: 90, textAlign: 'right' }}>Est. Atual</th>
              <th style={{ width: 90, textAlign: 'right' }}>Est. Minimo</th>
              <th style={{ width: 110, textAlign: 'right' }}>Est. Atual (Geral)</th>
              <th style={{ width: 110, textAlign: 'right' }}>Minimo de Compra</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((v) => {
              const attrs = grid.rowProps(v);
              const focada = celulaFocada === idLinha(v);
              const style = focada
                ? { ...attrs.style, background: '#fde68a', color: '#000', boxShadow: 'inset 3px 0 0 var(--accent)' }
                : attrs.style;
              // Celula de numero: o clique NAO pode subir pro grid (senao marcar a linha rouba o foco
              // do campo que a pessoa acabou de clicar pra digitar).
              const celulaNum = { onClick: (e: React.MouseEvent) => e.stopPropagation(), onDoubleClick: (e: React.MouseEvent) => e.stopPropagation(), style: { textAlign: 'right' as const } };
              return (
                <tr key={idLinha(v)} {...attrs} style={style}>
                  <td>
                    {v.foto_url
                      ? <img src={v.foto_url} alt="" className="produto-thumb" loading="lazy" />
                      : <div className="produto-thumb-vazio" />}
                  </td>
                  <td className="text-gray-500 font-bold no-underline">{v.codigo_interno}</td>
                  <td>
                    {v.descricao}
                    {v.status !== 'ativo' && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>(inativo)</span>}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {v.localizacao_codigo}
                      {/* A estrela aparece SOLIDA em toda principal (pedido do Ruan, 18/07). A 1a versao
                          apagava ela nos produtos de um lugar so ("principal por falta de concorrente")
                          — distincao minha que so servia pra sumir com a estrela justamente onde ela
                          e a maioria. Recurso que nao aparece nao existe. */}
                      {v.principal && (
                        <span title="Principal: e daqui que a venda baixa" style={{ background: '#fde68a', color: '#000', borderRadius: 3, padding: '1px 3px', display: 'inline-flex' }}>
                          <Star size={10} fill="#000" />
                        </span>
                      )}
                      {/* Sem principal e com mais de um lugar = a fila da faxina (DA-324). Ficar so sem
                          estrela nao distingue "nao e a principal" de "ninguem escolheu ainda". */}
                      {!v.principal && v.lugares_do_produto > 1 && (
                        <span title="Este produto tem varios lugares e ninguem marcou a vitrine" style={{ display: 'inline-flex', opacity: 0.35 }}>
                          <Star size={10} fill="none" stroke="currentColor" />
                        </span>
                      )}
                    </span>
                  </td>
                  <td {...celulaNum}>
                    <InputNum
                      valor={v.quantidade} nuloPermitido zeroVazio={false}
                      titulo="Quantas unidades tem NESTA prateleira. Vazio = ninguem contou."
                      onSalvar={(n) => salvarVinculo(v, 'quantidade', n)}
                      aoFocar={() => setCelulaFocada(idLinha(v))}
                      aoSair={() => setCelulaFocada((c) => (c === idLinha(v) ? null : c))}
                    />
                  </td>
                  <td {...celulaNum}>
                    <InputNum
                      valor={v.quantidade_minima} nuloPermitido zeroVazio={false}
                      // Sem minimo proprio, a prateleira HERDA o Minimo de Compra do produto ([DA-340]).
                      // O placeholder mostra o numero herdado no lugar do `—`, entao da pra ver de
                      // relance qual prateleira tem regra propria e qual esta seguindo o produto.
                      dica={num(v.estoque_minimo) > 0 ? fmtBR(v.estoque_minimo) : undefined}
                      titulo={v.quantidade_minima != null
                        ? 'Minimo proprio desta prateleira.'
                        : num(v.estoque_minimo) > 0
                          ? `Sem minimo proprio: herda o Minimo de Compra do produto (${fmtBR(v.estoque_minimo)}).`
                          : 'Sem minimo proprio, e o produto tambem nao tem Minimo de Compra.'}
                      onSalvar={(n) => salvarVinculo(v, 'quantidade_minima', n)}
                      aoFocar={() => setCelulaFocada(idLinha(v))}
                      aoSair={() => setCelulaFocada((c) => (c === idLinha(v) ? null : c))}
                    />
                  </td>
                  <td {...celulaNum}>
                    <InputNum
                      valor={v.estoque_atual} nuloPermitido={false} zeroVazio={false}
                      travado={!ehAdmin}
                      titulo={ehAdmin
                        ? 'Total do produto na loja inteira (o contabil). Nao e a soma das prateleiras.'
                        : 'Total do produto (o contabil). So o administrador altera — a quantidade se corrige pela CONTAGEM da prateleira, na coluna ao lado.'}
                      onSalvar={(n) => n !== null && salvarProduto(v, 'estoque_atual', n)}
                      aoFocar={() => setCelulaFocada(idLinha(v))}
                      aoSair={() => setCelulaFocada((c) => (c === idLinha(v) ? null : c))}
                    />
                  </td>
                  <td {...celulaNum}>
                    <InputNum
                      valor={v.estoque_minimo} nuloPermitido={false} zeroVazio
                      titulo="Abaixo disso o produto entra na Lista de Compras"
                      onSalvar={(n) => salvarProduto(v, 'estoque_minimo', n ?? 0)}
                      aoFocar={() => setCelulaFocada(idLinha(v))}
                      aoSair={() => setCelulaFocada((c) => (c === idLinha(v) ? null : c))}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Campo numerico da grade. Duas variacoes que MUDAM o significado do vazio:
//   `nuloPermitido` — apagar grava NULL (`—` = ninguem contou). E o dos campos da prateleira.
//   `zeroVazio`     — 0 se mostra vazio (`Minimo de Compra`: zero = ninguem definiu, DA-332).
// Sem separar os dois, apagar a contagem de uma prateleira gravaria `0` e o sistema passaria a
// dizer "conferido e vazio" — disparando reposicao por um numero que ninguem contou.
function InputNum({ valor, onSalvar, aoFocar, aoSair, nuloPermitido, zeroVazio, titulo, dica, travado }: {
  valor: string | null;
  onSalvar: (novo: number | null) => void;
  aoFocar: () => void;
  aoSair: () => void;
  nuloPermitido: boolean;
  zeroVazio: boolean;
  titulo: string;
  /** O que o campo vazio MOSTRA (valor herdado). Sem isso, `—`. */
  dica?: string;
  /** Trava a celula (mostra o numero, nao deixa digitar) — ver a coluna Est. Atual. */
  travado?: boolean;
}) {
  const mostrar = (v: string | null) => {
    if (v === null || v === '') return '';
    if (zeroVazio && num(v) === 0) return '';
    return fmtBR(v);
  };
  const [texto, setTexto] = useState(() => mostrar(valor));
  // Ressincroniza com o valor de fora (salvou / recarregou) so com o campo SEM foco — senao apaga
  // o que a pessoa esta digitando.
  const focadoRef = useRef(false);
  useEffect(() => {
    if (!focadoRef.current) setTexto(mostrar(valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  function commit() {
    focadoRef.current = false;
    const t = texto.trim().replace(',', '.');
    if (t === '') {
      if (!nuloPermitido) { setTexto(mostrar(valor)); return; }   // campo que nao aceita vazio: volta
      if (valor === null) { setTexto(''); return; }               // ja estava vazio: nada a fazer
      onSalvar(null);
      return;
    }
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) { setTexto(mostrar(valor)); return; }
    if (valor !== null && n === num(valor)) { setTexto(mostrar(valor)); return; }
    onSalvar(n);
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      className="cel-num"
      value={texto}
      placeholder={dica ?? '—'}
      title={titulo}
      readOnly={travado}
      onFocus={(e) => { focadoRef.current = true; aoFocar(); if (!travado) e.currentTarget.select(); }}
      onChange={(e) => { if (!travado) setTexto(e.target.value); }}
      onBlur={() => { if (!travado) commit(); aoSair(); }}
      style={{
        width: 70, textAlign: 'right', padding: '2px 6px',
        ...(travado ? { cursor: 'not-allowed', opacity: 0.65 } : {}),
      }}
    />
  );
}
